import {enableProdMode, ErrorHandler, APP_INITIALIZER, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {Router} from '@angular/router';
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {registerLocaleData, DatePipe} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import {DialogService, DynamicDialogModule} from 'primeng/dynamicdialog';
import {providePrimeNG} from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import {definePreset} from '@primeuix/themes';
import {ServiceWorkerModule} from '@angular/service-worker';

import {AppComponent} from './app/app.component';
import {AppRoutingModule} from './app/app-routing.module';
import {AppConfig} from './app/config/app-config.model';
import {APP_CONFIG} from './app/config/app-config.token';

import {AuthModule} from './app/auth/auth.module';
import {initializeModernAuth} from './app/auth/auth-initializer';
import {AuthService} from './app/auth/services/auth.service';
import {DeepLinkService} from './app/auth/services/deep-link.service';
import {MessageService} from './app/services/message.service';
import {AuthHeaderInterceptor} from './app/http-interceptors/auth-header.interceptor';
import {GlobalErrorHandler} from './app/services/global-error-handler';

// Register locales
registerLocaleData(localeFr);
registerLocaleData(localeNl);

// Custom PrimeNG theme with ABVV red colors
const MyABVVPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}'
    }
  }
});

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?cachebuster=v0004');
}

const CONFIG_URL = 'assets/config/config.json';

async function main() {
  const cfg: AppConfig = await fetch(CONFIG_URL, {cache: 'no-store'})
    .then(r => {
      if (!r.ok) throw new Error(`Failed to load ${CONFIG_URL} (${r.status})`);
      return r.json();
    });

  // Validate required config keys
  const required = ['apiGatewayUrl', 'keycloakRootUrl', 'keycloakContext', 'client'];
  const missing = required.filter(k => !(k in cfg));
  if (missing.length) throw new Error(`Config missing required keys: ${missing.join(', ')}`);

  if (cfg.production) {
    enableProdMode();
  } else if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    // Non-production builds must not be served by a stale Service Worker left over
    // from a previous production session. Unregister any existing worker and reload
    // once so the fresh assets come straight from nginx.
    const regs = await navigator.serviceWorker.getRegistrations();
    if (regs.length > 0) {
      await Promise.all(regs.map(r => r.unregister()));
      window.location.reload();
      return;
    }
  }

  // Base imports (inside main() to access cfg for ServiceWorker)
  const baseImports = [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    AuthModule,
    DynamicDialogModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: cfg.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ];

  // Common providers
  const commonProviders = [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideAnimations(), // TODO: Deprecated in Angular 20.2, will be removed in v23. Migrate to animate.enter/animate.leave
    provideHttpClient(withInterceptorsFromDi()),
    DatePipe,
    providePrimeNG({
      theme: {
        preset: MyABVVPreset,
        options: {
          darkModeSelector: '.dark'
        }
      },
      ripple: false,
      overlayOptions: {
        showTransitionOptions: '0ms',
        hideTransitionOptions: '0ms'
      }
    }),
    DialogService,
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    {provide: HTTP_INTERCEPTORS, useClass: AuthHeaderInterceptor, multi: true},
  ];

  // APP_INITIALIZER based on auth mode
  const authInitializer = {
    provide: APP_INITIALIZER,
    useFactory: initializeModernAuth,
    multi: true,
    deps: [AuthService, DeepLinkService, MessageService, Router, APP_CONFIG]
  };

  // All providers combined
  const providers = [
    {provide: APP_CONFIG, useValue: cfg},
    importProvidersFrom(...baseImports),
    ...commonProviders,
    authInitializer,
  ];

  // Bootstrap the application
  await bootstrapApplication(AppComponent, {providers});
}

main().catch((err) => console.error(err));
